/**
 * List Module.
 * AJAX-based list interaction, including filters and pagination.
 *
 * Implementation example:
 * ```
 * $('.en-mdl-list').each(function () {
 *     var $list = $(this);
 *     $list.data('list', new EN.List($list));
 * });
 * ```
 *
 * @class List
 * @author EN GARDE (David Hell)
 * @version 1.4
 *
 * @param {jQuery} $element - single root element
 * @param {object} [options] - Overrides defaults, see below.
 * @property {boolean} ajaxUrl=undefined - where to send ajax request to. set only if you want to overwrite internal logic (action-attribute > EN.config.current_url > EN.config.base_url > '?')
 * @property {string} eventNamespace='list' – namespace for all events
 * @property {number} firstPage=1 – index of first page. usually 1 or 0
 * @property {boolean} enableHistory=!!(window.history && history.pushState) – enable history api?
 * @property {number} debounceDelay=600 – milliseconds to wait after filter changes
 * @property {object} selectors – Override default selectors, see below
 * @property {string} selectors.nothingFound='.en-mdl-list__nothing-found' – nothing found module selector
 * @property {string} selectors.submoduleContainer='.en-mdl-list__submodules' – submodule container selector
 * @property {string} selectors.filterForm='.en-mdl-list_filter' – filter form selector (usually the filte rmodule itself)
 * @property {string} selectors.filterCanBeWithoutResults='.en-filter__item[data-en-filter-name]' – filters that can be part of the " * terms_without_results" ajax return array
 * @property {string} selectors.paging='.en-mdl-paging' – paging module selector
 * @property {string} selectors.pagingPrev='.en-mdl-paging__item--prev' – paging previous selector
 * @property {string} selectors.pagingNext='.en-mdl-paging__item--next' – paging next selector
 * @property {string} selectors.count='.en-mdl-list_filter__counts' – counter selector
 * @property {string} selectors.nestedLayer1Elements='.en-mdl-list_filter__nested-layer-1 > .en-filter__item[data-en-filter-name]' – all elements on layer 1 (must have attributes as defined in attributeNames.filterName and attributeNames.filterItemId)
 * @property {string} selectors.nestedLayer2Elements='.en-mdl-list_filter__nested-layer-2 > .en-filter__item[data-en-filter-name]' – all elements on layer 2 (must have attributes as defined in attributeNames.filterName and attributeNames.filterItemId)
 * @property {object} classNames – Override default class names, see below
 * @property {string} classNames.locked='en-is-loading' – class on $element while locked
 * @property {string} classNames.disabled='en-is-disabled' – disabled elements (e.g. pagination)
 * @property {string} classNames.active='en-is-active' – active class (e.g. for filters)
 * @property {string} classNames.hidden='en-hidden' – hidden elements (usually display: none)
 * @property {string} classNames.noResult='en-is-disabled' – class for terms without results
 * @property {object} attributeNames – Override default attribute names, see below
 * @property {string} attributeNames.ajaxData='data-en-mdl-ajax-data' – ajax data template that will be sent with each request (and kept up to date)
 * @property {string} attributeNames.ajaxMetaData='data-en-mdl-ajax-meta-data' – meta data, contains basic information about the list
 * @property {string} attributeNames.filterName='data-en-filter-name' – used to identify each filter
 * @property {string} attributeNames.filterItemId='data-en-filter-item-id' – value of the filter
 *
 *
 * This module emits many events
 * Use them to modify variables or to run your custom code.
 * A data object, containing variables and a reference to the current module instance, is passed with every event
 *
 * Event binding example:
 * ```
 * $(document).on('beforeRequest.list', function(e, data) {
 *     console.log(data, data.instance);
 * });
 * ```
 *
 * Available events:
 *  - beforeInit
 *  - afterInit
 *  - beforeRequest (data: { newData, options })
 *  - beforeSendRequest (data: { newData, options })
 *  - beforeUpdateDom (data: { newData, options, replaceExisting, insertBefore, urlData })
 *  - afterUpdateSubmodules (data: { $items, replaceExisting, insertBefore })
 *  - afterRequest (data: { success, newData, options[, replaceExisting][, insertBefore][, urlData] })
 *  - changeLockStatus (use data.instance.isLocked() to find out new lock status)
 *
 *
 * @requires lodash
 * @requires jQuery
 * @requires JsHelper
 * @requires UrlHelper
 * @requires DomHelper
 * @requires StringTemplate
 *
 * Changelog:
 * 	1.4		fixed bug that caused a reload on every hashchange
 * 	1.3		fixed history back to black state (no list data in url)
 * 	1.2		added beforeSendRequest
 * 	1.1		updated ajax url
 * 	1.0		initial version
 *
 */


var EN = EN || {};

EN.List = (function (_, $, JsHelper, UrlHelper, DomHelper, StringTemplate) {
	'use strict';

			//console.log($layer2ElementsActive);
	/** @constructor */
	function List($element, options) {
		var that = this;

		// dependency checks
		if (!JsHelper.dependencyExists(_, 'lodash', 'List')) return;
		if (!JsHelper.dependencyExists($, 'jQuery', 'List')) return;
		if (!JsHelper.dependencyExists(JsHelper, 'JsHelper', 'List')) return;
		if (!JsHelper.dependencyExists(UrlHelper, 'UrlHelper', 'List')) return;
		if (!JsHelper.dependencyExists(DomHelper, 'DomHelper', 'List')) return;
		if (!JsHelper.dependencyExists(StringTemplate, 'StringTemplate', 'List')) return;

		// bail if no element was passed
		if (!$element || !$element.length) {
			return;
		}

		that.options = _.defaultsDeep(options, that.defaults);
		that.$element = $element.first();
		that.data = {};
		that.locked = false;

		$(function() {
			that.init();
		});
	}

	// defaults
	List.prototype.defaults = {
		ajaxUrl: undefined,
		eventNamespace: '.list',
		firstPage: 1,
		enableHistory: !!(window.history && history.pushState),
		debounceDelay: 600,
		selectors: {
			nothingFound: '.en-mdl-list__nothing-found',
			submoduleContainer: '.en-mdl-list__submodules',
			filterForm: '.en-mdl-list_filter',
			filterCanBeWithoutResults: '.en-filter__item[data-en-filter-name]',
			paging: '.en-mdl-paging',
			pagingPrev: '.en-mdl-paging__item--prev',
			pagingNext: '.en-mdl-paging__item--next',
			count: '.en-mdl-list__count',
			nestedLayer1Elements: '.en-mdl-list_filter__nested-layer-1 > .en-filter__item[data-en-filter-name]',
			nestedLayer2Elements: '.en-mdl-list_filter__nested-layer-2 > .en-filter__item[data-en-filter-name]'
		},
		classNames: {
			locked: 'en-is-loading',
			disabled: 'en-is-disabled',
			active: 'en-is-active',
			hidden: 'en-hidden',
			noResult: 'en-is-disabled',
			initialized: 'en-is-initialized'
		},
		attributeNames: {
			ajaxData: 'data-en-mdl-ajax-data',
			ajaxMetaData: 'data-en-mdl-ajax-meta-data',
			filterName: 'data-en-filter-name',
			filterItemId: 'data-en-filter-item-id'
		}
	};

	/**
	 * Initialize list
	 * @memberof List
	 */
	List.prototype.init = function () {
		var that = this;

		// load data and meta data from dom
		that.ajaxMetaData = $.parseJSON(that.$element.attr(that.options.attributeNames.ajaxMetaData));
		that.ajaxData = $.parseJSON(that.$element.attr(that.options.attributeNames.ajaxData));
		// that.data = that.getFormData() || {};
		that.data = {};
		that.data.page = that.ajaxData.page || that.options.firstPage;

		that.triggerEvent('beforeInit');

		that.initEvents();
		that.initNestedFilters();

		that.$element.addClass(that.options.classNames.initialized);
		that.triggerEvent('afterInit');
	};

	/**
	 * Initialize events
	 * @memberof List
	 */
	List.prototype.initEvents = function () {
		var that = this;
		var formSelector = that.options.selectors.filterForm;

		// remove eventually existing event handlers
		that.$element.off('.' + that.options.eventNamespace);

		// handle form submission
		that.$element.on(JsHelper.namespaceEvents('submit', that.options.namespace), formSelector, function (e) {
			var data = that.getFormData();
			data.page = that.options.firstPage; // filters should reset page to first
			that.request(data, { cancelPending: true, forceReplace: true });
			return false; // prevent default
		});

		// trigger form submission whenever an input changes
		that.$element.on(JsHelper.namespaceEvents('input change reset',  that.options.namespace), formSelector, _.debounce(function () {
			$(this).submit();
		}, that.options.debounceDelay, false));

		// on reset, remove all active classes
		// that.$element.on(JsHelper.namespaceEvents('input change reset', that.options.namespace), formSelector, function () {
		// 	$(this).find('.' + that.options.classNames.active).removeClass(that.options.classNames.active);
		// });
		that.$element.on(JsHelper.namespaceEvents('click', that.options.namespace), that.options.selectors.filterCanBeWithoutResults, _.debounce(function () {
			
			var $current = $(this); 
			if (!($(this).hasClass(that.options.classNames.active))) {
				$(formSelector).find('.' + that.options.classNames.active).removeClass(that.options.classNames.active);
				$current.addClass(that.options.classNames.active);
			}

		}, that.options.debounceDelay, false));

		that.$element.on(JsHelper.namespaceEvents('click', that.options.namespace), that.options.selectors.pagingPrev + ',' + that.options.selectors.pagingNext, function (e) {
			var $this = $(this);

			// allow user to open a new tab
			if (e.ctrlKey === true || e.metaKey === true) return;

			e.preventDefault();

			// bail if disabled
			if ($this.hasClass(that.options.classNames.disabled)) return;

			if ($this.filter(that.options.selectors.pagingPrev).length !== 0) that.prev();
			else that.next();
		});

		if (that.options.enableHistory) {
			$(window).on(JsHelper.namespaceEvents('popstate', that.options.namespace), function(e) {
				// attention: this event is also fired, when SETTING a new hash
				// [1] read url
				// [2] do not force reload (because it is possible that there are no changes in the list (e.g.: on hashchange))
				// [3] update list
				// [4] update form accordingly
				var data = that.getDataFromCurrentUrl() || {}; // [1]
				data.page = data.page || that.options.firstPage;

				var options = {
					cancelPending: true,
					forceReplace: true,
					forceReload: false, // [2]
					createNewState: true
				};

				that.request(data, options); // [3]
				that.setFormData(data); // [4]
			});
		}
	};

	/**
	 * Initialize nested filters
	 * use radio buttons if you want highlander functionality
	 * otherwise use checkboxes
	 * @memberof List
	 */
	List.prototype.initNestedFilters = function () {
		var that = this;
		var formSelector = that.options.selectors.filterForm;
		var $form = that.$element.find(that.options.selectors.filterForm);
		if ($form.length === 0) return;

		var updateNestedLayer2 = function($form) {
			var $layer1Elements = $form.find(that.options.selectors.nestedLayer1Elements);
			var $layer2Elements = $form.find(that.options.selectors.nestedLayer2Elements);
			if ($layer1Elements.length === 0 || $layer2Elements.length === 0) return;

			var $layer1ElementsActive = $layer1Elements.find('input:checked').closest($layer1Elements);

			var filters = $layer1ElementsActive.map(function () {
				var $element = $(this);
				return	'[' + that.options.attributeNames.filterName + '="' + $element.attr(that.options.attributeNames.filterName) + '"]' +
						'[' + that.options.attributeNames.filterItemId + '="' + $element.attr(that.options.attributeNames.filterItemId) + '"]';
			}).get().join(',');
			var $layer2ElementsActive = $layer2Elements.filter(filters);

			$layer2ElementsActive
				.addClass(that.options.classNames.active)
				.find('input')
					.prop('disabled', false);

			$layer2Elements.not($layer2ElementsActive)
				.removeClass(that.options.classNames.active)
				.find('input')
					.prop('disabled', true)
					.prop('checked', false);
		};


		// init
		updateNestedLayer2($form);

		// listen to changes
		that.$element.on(JsHelper.namespaceEvents('change',  that.options.namespace), that.options.selectors.nestedLayer1Elements, function (e) {
			updateNestedLayer2($form);
		});

		// reset when the form is reset (must be slightly delayed)
		that.$element.on(JsHelper.namespaceEvents('reset',  that.options.namespace), that.options.selectors.formSelector, function (e) {
			setTimeout(function() {
				updateNestedLayer2($form);
			}, 100);
		});

	};

	/**
	 * Get url string based on current url and new parameters from data object
	 * @memberof List
	 * @param {object} data
	 * @return {string} url
	 */
	List.prototype.getUrlFromData = function (data) {
		var that = this;
		var _data = UrlHelper.getQueryParameters();
		var baseUrl = '?';

		// remove ajax action parameter
		_.unset(data, that.ajaxMetaData.action_attribute_name);

		_data[that.ajaxMetaData.list_data_name] = that.getMappedData(data);

		return baseUrl + UrlHelper.encodeParameters(_data);
	};

	/**
	 * Get data object from current url
	 * @memberof List
	 * @return {object} data
	 */
	List.prototype.getDataFromCurrentUrl = function () {
		var that = this;
		var _data = UrlHelper.getQueryParameters();

		var data = that.getUnmappedData(_data[that.ajaxMetaData.list_data_name]);

		// convert page to int
		if (!_.isUndefined(data.page)) data.page = parseInt(data.page);

		return data;
	};

	/**
	 * Rewrite data object based on attribute name mapping in ajax meta data
	 * @memberof List
	 * @param {object} data – original data object
	 * @return {object} the converted object
	 */
	List.prototype.getMappedData = function (data) {
		var that = this;
		var returnData = {};
		var mapping = that.ajaxMetaData;
		_.forEach(data, function (value, key) {
			if (!_.isEmpty(mapping[key + '_attribute_name'])) {
				returnData[mapping[key + '_attribute_name']] = value;
			}
			else {
				returnData[key] = value;
			}
		});
		return returnData;
	};

	/**
	 * Un-Rewrite data object based on attribute name mapping in ajax meta data
	 * @memberof List
	 * @param {object} data – original data object
	 * @return {object} the converted object
	 */
	List.prototype.getUnmappedData = function (data) {
		var that = this;
		var returnData = {};
		var mapping = _.invert(_.extend({}, that.ajaxMetaData));
		_.forEach(data, function (value, key) {
			if (!_.isEmpty(mapping[key])) {
				returnData[mapping[key].replace('_attribute_name', '')] = value;
			}
			else {
				returnData[key] = value;
			}
		});
		return returnData;
	};

	List.prototype.getFormData = function () {
		var that = this;
		var $form = that.$element.find(that.options.selectors.filterForm);
		if ($form.length === 0) return {};
		var data = DomHelper.getFormData($form);
		return data && data[that.ajaxMetaData.list_data_name] ? data[that.ajaxMetaData.list_data_name] : {};
	};

	List.prototype.setFormData = function (data) {
		var that = this;
		var $form = that.$element.find(that.options.selectors.filterForm);
		if ($form.length === 0) return {};
		var _data = {};
		_data[that.ajaxMetaData.list_data_name] = data;
		DomHelper.setFormData($form, _data, true);
	};

	/**
	 * Ask the list to update its content
	 * @memberof List
	 * @param {object} newData – data for ajax request
	 * @param {object} options – overwrite default request options, see below
	 * @property {boolean} cancelPending=false – abort currently running requests and start a new one
	 * @property {boolean} forceReplace=false – replace items no matter what behavior says
	 * @property {boolean} forceReload=false – reload even if data is same as current
	 * @property {boolean} createNewState=false – is the current request coming from history? yes -> url will not be updated
	 * @return {boolean} – did send out new request? (does not necessarily mean that this request will be successful, request is saved in that.xhrRequest)
	 */
	List.prototype.request = function (newData, options) {
		// [1]
		var that = this;
		var oldData = that.data;
		var mergedData;
		var ajaxRequestData = {};
		options = _.defaults(options, {
			cancelPending: false,
			forceReplace: false,
			forceReload: false,
			createNewState: false
		});

		// console.log('request', newData, options);

		that.triggerEvent('beforeRequest', { newData: newData, options: options });

		// force refresh (cancel pending request)
		if (options.cancelPending === true) {
			if (that.xhrRequest && that.xhrRequest.readystate != 4) that.xhrRequest.abort();
			that.unlock();
		}

		// do not send any requests if the requested data is already here
		if (options.forceReload !== true && _.isEqual(newData, oldData)) {
			return false;
		}

		// do nothing if another request is still pending
		if (that.isLocked()) return false;
		that.lock();

		// setup ajax data object
		ajaxRequestData[that.ajaxMetaData.list_data_name] = that.getMappedData(_.extend({}, that.getAjaxData(), newData));

		// send request
		that.triggerEvent('beforeSendRequest', { newData: newData, options: options });

		that.xhrRequest = $.ajax({
			url: that.getAjaxUrl(),
			type: 'GET',
			data: ajaxRequestData,
			dataType: 'json'
		})
		.done(function(rData) {
			// handle ajax return:
			// [1] determine whether to replace or append new items
			// [2] insert before only if action = back
			// [3] prepare data for new url
			// [4] remove certain variables from url
			// [4.1] never save page when appending (reload would not start from the beginning)
			// [5] replace/append items
			// [6] enable/disable terms
			// [7] update counter
			// [8] update pagination elements
			// [9] create new state in history
			// [10] save data object for reference in next request
			var replaceExisting = options.forceReplace || rData.behavior === 'replace'; // [1]
			var insertBefore = oldData.page > newData.page; // [2]
			var urlData = _.extend({}, newData);

			if (rData.behavior === 'append') {
				_.unset(urlData, 'page'); // [4.1]
			}

			that.triggerEvent('beforeUpdateDom', {
				newData: newData,
				options: options,
				replaceExisting: replaceExisting,
				insertBefore: insertBefore,
				urlData: urlData
			});

			that.updateSubmodules(rData.items_html, replaceExisting, insertBefore); // [5]
			that.updateTerms(rData.terms_without_results); // [6]
			that.updateCount(rData.count_reduced, rData.count_total); // [7]
			that.updatePaging(newData.page, rData.has_paging && rData.has_paging_next, rData.has_paging && rData.has_paging_previous); // [8]

			if (options.createNewState !== true) that.updateUrl(urlData); // [9]
			that.saveData(newData); // [10]

			that.triggerEvent('afterRequest', {
				success: true,
				newData: newData,
				options: options,
				replaceExisting: replaceExisting,
				insertBefore: insertBefore,
				urlData: urlData
			});

			that.unlock();
		})
		.fail(function(rData) {
			that.unlock();
			that.triggerEvent('afterRequest', {
				success: false,
				newData: newData,
				options: options
			});
		});

		return true;
	};

	/**
	 * Go to a specified page
	 * @memberof List
	 * @param {number} page
	 * @return {boolean} – did trigger a new request?
	 */
	List.prototype.goToPage = function (page) {
		var that = this;
		if (that.isLocked()) return;

		var data = that.getData();
		data.page = page;
		return that.request(data);
	};

	/**
	 * Go to the previous page
	 * @memberof List
	 * @return {boolean} – did trigger a new request?
	 */
	List.prototype.prev = function () {
		var that = this;
		if (that.isLocked()) return;

		var data = that.getData();
		data.page = Math.max(this.options.firstPage, data.page - 1);
		return that.request(data);
	};

	/**
	 * Go to the next page
	 * @memberof List
	 * @return {boolean} – did trigger a new request?
	 */
	List.prototype.next = function () {
		var that = this;
		if (that.isLocked()) return;

		var data = that.getData();
		data.page = Math.max(this.options.firstPage, data.page + 1);
		return that.request(data);
	};

	/**
	 * Prevent further requests and set list into a loading state
	 * @memberof List
	 */
	List.prototype.lock = function () {
		var that = this;
		that.locked = true;
		that.$element.addClass(that.options.classNames.locked);
		that.triggerEvent('changeLockStatus');
	};

	/**
	 * Allow requests and remove loading state
	 * @memberof List
	 */
	List.prototype.unlock = function () {
		var that = this;
		that.locked = false;
		that.$element.removeClass(that.options.classNames.locked);
		that.triggerEvent('changeLockStatus');
	};

	/**
	 * Get current lock status
	 * @memberof List
	 * @return {boolean} – is the list currently locked
	 */
	List.prototype.isLocked = function () {
		var that = this;
		return !!that.locked;
	};

	/**
	 * Update submodules based on ajax result
	 * @memberof List
	 * @param {string} itemsHtml – html string containing submodules
	 * @param {boolean} replaceExisting=true – remove old elements before inserting new ones?
	 * @param {boolean} insertBefore=false – add new ones before existing ones (only when not replacing old elements)?
	 */
	List.prototype.updateSubmodules = function (itemsHtml, replaceExisting, insertBefore) {
		var that = this;
		replaceExisting = replaceExisting === false ? false : true;
		insertBefore = insertBefore === true ? true : false;

		var $nothingFoundModule = that.$element.find(that.options.selectors.nothingFound).first();
		var $submodulesContainer = that.$element.find(that.options.selectors.submoduleContainer);
		var $items = itemsHtml ? $('<div />').html(itemsHtml).children() : null;


		// no new items
		if (!$items || !$items.length) {
			$items = $nothingFoundModule
				.addClass(that.options.classNames.hidden)
				.clone()
				.removeClass(that.options.classNames.hidden);
		}

		if (replaceExisting) {
			$submodulesContainer.html($items);
		}
		else {
			if (insertBefore) $submodulesContainer.prepend($items);
			else $submodulesContainer.append($items);
		}

		that.triggerEvent('afterUpdateSubmodules', {
			$items: $items,
			replaceExisting: replaceExisting,
			insertBefore: insertBefore
		});
	};

	/**
	 * Update term based filters in DOM
	 * @memberof List
	 * @param {object} emptyTerms – object of arrays, keyed by term name
	 */
	List.prototype.updateTerms = function (emptyTerms) {
		var that = this;
		var formSelector = that.options.selectors.filterForm;
		var $form = that.$element.find(formSelector);

		$form.find(that.options.selectors.filterCanBeWithoutResults).each(function(i, old) {
			var $item = $(this);
			var filterName = $item.attr(that.options.attributeNames.filterName);
			var filterItemId = parseInt($item.attr(that.options.attributeNames.filterItemId));

			// is in empty terms array?
			if (!_.isEmpty(emptyTerms[filterName]) && _.indexOf(emptyTerms[filterName], filterItemId) !== -1) {
				$item.addClass(that.options.classNames.noResult);
			}
			// otherwise reset
			else {
				$item.removeClass(that.options.classNames.noResult);
			}
		});
	};

	/**
	 * Update count
	 * @memberof List
	 * @param {number} reduced – reduced amount if subitems
	 * @param {number} total – total amount if subitems
	 */
	List.prototype.updateCount = function (reduced, total) {
		var that = this;
		reduced = parseInt(reduced);
		total = parseInt(total);

		var metaData = that.ajaxMetaData;
		var $count = that.$element.find(that.options.selectors.count);

		if ($count.length === 0 || _.isEmpty(metaData)) return;

		var tpl = new StringTemplate();
		tpl.setVar();

		tpl.setVar('count_reduced', reduced);
		tpl.setVar('count_total', total);
		tpl.setVar('items_label_singular', metaData.items_label_singular);
		tpl.setVar('items_label_plural', metaData.items_label_plural);

		// singular | plural based on reduced count
		tpl.setVar('items_label_reduced', reduced === 1 ? metaData.items_label_singular : metaData.items_label_plural);

		// singular | plural based on total count
		tpl.setVar('items_label_total', total === 1 ? metaData.items_label_singular : metaData.items_label_plural);

		$count.html(tpl.getParsedTemplateString(metaData.count_pattern));
	};

	/**
	 * Update paging module
	 * @memberof List
	 * @param {number} page – current page
	 * @param {boolean} hasNext – is there a page after the current one
	 * @param {boolean} hasPrev – is there a page before the current one
	 */
	List.prototype.updatePaging = function (page, hasNext, hasPrev) {
		var that = this;

		var $paging = that.$element.find(that.options.selectors.paging);
		var $pagingNext = that.$element.find(that.options.selectors.pagingNext);
		var $pagingPrev = that.$element.find(that.options.selectors.pagingPrev);
		var disabledClass = that.options.classNames.disabled;
		var data = that.getFormData();

		if (!hasNext) {
			$pagingNext
				.addClass(disabledClass)
				.attr('href', '');
		}
		else {
			data.page = page + 1;

			$pagingNext
				.removeClass(disabledClass)
				.attr('href', that.getUrlFromData(data));
		}

		if (!hasPrev) {
			$pagingPrev
				.addClass(disabledClass)
				.attr('href', '');
		}
		else {
			data.page = Math.max(this.options.firstPage, page - 1);
			$pagingPrev
				.removeClass(disabledClass)
				.attr('href', that.getUrlFromData(data));
		}

		if (!hasNext && !hasPrev) $paging.addClass(that.options.classNames.hidden);
		else $paging.removeClass(that.options.classNames.hidden);
	};

	/**
	 * Save state to url
	 * @memberof List
	 * @param {object} data – object of key, value pairs
	 */
	List.prototype.updateUrl = function (data) {
		var that = this;

		if (!that.options.enableHistory) {
			return false;
		}

		history.pushState(undefined, undefined, that.getUrlFromData(data));
	};

	/**
	 * Update ajax data-attribute
	 * @memberof List
	 * @param {object} data
	 */
	List.prototype.saveAjaxDataToDom = function (data) {
		var that = this;
		var ajaxData;
		var domData = $.parseJSON(that.$element.attr(that.options.attributeNames.ajaxData));
		_.forEach(domData, function (value, key) {
			if (!_.isEmpty(data[key])) {
				domData[key] = data[key];
			}
		});
		that.$element.attr(that.options.attributeNames.ajaxData, JSON.stringify(domData));
	};

	List.prototype.getData = function () {
		var that = this;
		return _.extend({}, that.data);
	};
	List.prototype.getAjaxData = function () {
		var that = this;
		return _.extend({}, that.ajaxData);
	};
	List.prototype.saveData = function (data) {
		var that = this;
		that.data = data;

		that.saveAjaxDataToDom(that.data);
	};

	/**
	 * Trigger event. Use these events for external hooks.
	 * @memberof List
	 * @param {string} name – event name (will be namespaced)
	 * @param {object} data – event data. instance will be added automatically
	 */
	List.prototype.triggerEvent = function(name, data) {
		var that = this;
		data = data || {};
		data.instance = that;
		that.$element.trigger(JsHelper.namespaceEvents(name, that.options.namespace), data);
	};

	/**
	 * Get URL to send all AJAX requests to
	 * Uses options.ajaxUrl
	 * if undefined -> EN.config.current_url
	 * if undefined -> EN.config.base_url
	 * if undefined -> ''
	 * @memberof List
	 * @return {string} – the url
	 */
	List.prototype.getAjaxUrl = function() {
		var that = this;
		var ajaxUrl = that.options.ajaxUrl;
		var urlParts;
		var queryParams;

		if (ajaxUrl === undefined) {
			var $form = that.$element.find(that.options.selectors.filterForm);
			ajaxUrl = $form.attr('action');
		}

		if (ajaxUrl === undefined && typeof that.ajaxMetaData.ajax_url !== 'undefined') {
			ajaxUrl = that.ajaxMetaData.ajax_url;
		}

		if (ajaxUrl === undefined && EN.config && EN.config.current_url) {
			ajaxUrl = EN.config.current_url;
		}

		if (ajaxUrl === undefined && EN.config && EN.config.base_url) {
			ajaxUrl = EN.config.base_url;
		}

		if (ajaxUrl === undefined) {
			ajaxUrl = '?'; // current url without any GET-Params
		}

		return ajaxUrl;
	};

	return List;
}(lodash, jQuery, EN.JsHelper, EN.UrlHelper, EN.DomHelper, EN.StringTemplate));
